/**
 * SessionError — the "couldn't connect" curtain the app shell renders when the
 * anonymous-session bootstrap has persistently failed (session.status ===
 * "error"). It self-gates on that status and overlays the viewport, so the app
 * shell mounts it once, unconditionally, beside the router: whatever route the
 * user cold-loaded stays mounted and route-resolved behind the curtain, so a
 * recovered session reveals it in place rather than remounting to the default.
 *
 * Zero properties, zero custom events: the retry gesture travels out through
 * the sessionRetry$ channel (session.state.ts) that session.sync.ts owns,
 * keeping this view I/O-free like every other .view.
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { css, html } from "lit";
import { state } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { distinctUntilChanged, map, takeUntil } from "rxjs";
import { LocalizeMixin } from "../i18n/localize.ts";
import { session, sessionRetry$ } from "./session.state.ts";

@schmancyElement("session-error")
export class SessionError extends LocalizeMixin(SchmancyElement) {
	// Contributes no box while dormant; the active curtain is a fixed child that
	// positions against the viewport regardless, so this host never disturbs the
	// router's own layout.
	static styles = [css`:host { display: contents; }`];

	@state() private retrying = false;

	override connectedCallback() {
		super.connectedCallback();
		session.$.pipe(
			map((current) => current.status),
			distinctUntilChanged(),
			takeUntil(this.disconnecting),
		).subscribe((status) => {
			if (status !== "loading") this.retrying = false;
		});
	}

	override render() {
		return when(
			session.value.status === "error" ||
				(this.retrying && session.value.status === "loading"),
			() => html`
				<schmancy-surface
					role="alert"
					class="fixed inset-0 z-50 flex items-center justify-center p-6"
					type="containerLowest"
					opacity="solid"
				>
					<div class="flex w-full max-w-sm flex-col items-start gap-3 text-start">
						<schmancy-icon class="text-5xl text-error-default">cloud_off</schmancy-icon>
						<schmancy-typography type="headline" token="sm">
							${this.t("auth.connection.title")}
						</schmancy-typography>
						<schmancy-typography type="body" token="md" class="max-w-sm text-surface-onVariant">
							${this.t("auth.connection.body")}
						</schmancy-typography>
						<schmancy-button variant="filled" class="mt-3" .busy=${this.retrying} @click=${() => {
							if (this.retrying) return;
							this.retrying = true;
							sessionRetry$.next();
						}}>
							${this.t("auth.connection.retry")}
						</schmancy-button>
					</div>
				</schmancy-surface>
			`,
		);
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"session-error": SessionError;
	}
}
